import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { Phone, CalendarCheck, Clock, Users, Snowflake, Sun } from "lucide-react";
import { Button } from "../components/ui/button";

export const ReservationSection = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="reservation"
      data-testid="reservation-section"
      className="py-24 md:py-36 px-6 md:px-12 bg-[#FDFBF7] relative overflow-hidden"
    >
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-[#F5E6D3]/30 -skew-x-12 translate-x-20 pointer-events-none" />

      <div className="max-w-5xl mx-auto relative" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <p
              className="text-sm uppercase tracking-[0.3em] text-[#C1624D] mb-4"
              style={{ fontFamily: "'Lato', sans-serif" }}
            >
              {t("reservation.label")}
            </p>
            <h2
              className="text-4xl md:text-5xl font-light tracking-tight text-[#2C2420] mb-6 leading-[1.1]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="reservation-heading"
            >
              {t("reservation.heading")}
            </h2>
            <p
              className="text-base md:text-lg text-[#5D544F] leading-relaxed mb-8"
              style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
            >
              {t("reservation.subtitle")}
            </p>

            {/* Info cards */}
            <div className="space-y-4 mb-10">
              {[
                {
                  icon: Clock,
                  title: t("reservation.hours_title"),
                  desc: t("reservation.hours_value"),
                  detail: t("reservation.hours_detail"),
                },
                {
                  icon: Users,
                  title: t("reservation.groups_title"),
                  desc: t("reservation.groups_value"),
                  detail: t("reservation.groups_detail"),
                },
                {
                  icon: CalendarCheck,
                  title: t("reservation.events_title"),
                  desc: t("reservation.events_value"),
                  detail: t("reservation.events_detail"),
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#C1624D]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C1624D]/20 transition-colors duration-300">
                    <item.icon size={18} className="text-[#C1624D]" />
                  </div>
                  <div>
                    <p
                      className="text-sm font-medium text-[#2C2420]"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      {item.title}
                    </p>
                    <p
                      className="text-sm text-[#5D544F]"
                      style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                    >
                      {item.desc}
                    </p>
                    <p
                      className="text-xs text-[#8C847F] mt-0.5"
                      style={{ fontFamily: "'Lato', sans-serif" }}
                    >
                      {item.detail}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: CTA card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-white/80 backdrop-blur-sm p-10 md:p-12 border border-[#D8D0C5] rounded-sm shadow-sm text-center">
              <div className="w-16 h-16 rounded-full bg-[#C1624D]/10 flex items-center justify-center mx-auto mb-6">
                <Phone size={28} className="text-[#C1624D]" />
              </div>
              <h3
                className="text-2xl md:text-3xl font-light text-[#2C2420] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Appelez-nous
              </h3>
              <a
                href="tel:+33490460984"
                className="block text-3xl md:text-4xl font-light text-[#C1624D] hover:opacity-80 transition-opacity duration-300"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="reservation-phone"
              >
                04 90 46 09 84
              </a>
              <a
                href="tel:+33634134035"
                className="block text-xl md:text-2xl font-light text-[#8B6F4E] mb-4 hover:opacity-80 transition-opacity duration-300"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="reservation-phone-mobile"
              >
                06 34 13 40 35
              </a>
              <p
                className="text-sm text-[#8C847F] mb-8"
                style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
              >
                Du mercredi au dimanche, aux heures d'ouverture
              </p>

              {/* Amenities */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 mb-8" data-testid="reservation-amenities">
                <span
                  className="inline-flex items-center gap-2 text-sm text-[#5D544F]"
                  style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                >
                  <Snowflake size={15} className="text-[#A5C4D4]" />
                  {t("reservation.amenities_clim")}
                </span>
                <span
                  className="inline-flex items-center gap-2 text-sm text-[#5D544F]"
                  style={{ fontFamily: "'Lato', sans-serif", fontWeight: 300 }}
                >
                  <Sun size={15} className="text-[#C1624D]" />
                  {t("reservation.amenities_terrasse")}
                </span>
              </div>

              <div className="w-full h-px bg-[#D8D0C5] mb-8" />

              {/* Coming soon badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#6F7D4E]/10 mb-4">
                <CalendarCheck size={16} className="text-[#6F7D4E]" />
                <p
                  className="text-sm text-[#6F7D4E]"
                  style={{ fontFamily: "'Lato', sans-serif", fontWeight: 400 }}
                  data-testid="reservation-coming-soon"
                >
                  {t("reservation.coming_soon")}
                </p>
              </div>
              <p
                className="text-xs text-[#8C847F]"
                style={{ fontFamily: "'Lato', sans-serif" }}
              >
                {t("reservation.coming_soon_desc")}
              </p>

              <div className="mt-8">
                <Button
                  asChild
                  className="w-full py-3 rounded-full bg-[#C1624D] text-white font-serif italic text-lg
                             hover:bg-[#A6503C] active:scale-[0.98] transition-all duration-300 shadow-md hover:shadow-lg h-auto"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  <a href="tel:+33490460984" data-testid="reservation-call-btn">
                    {t("reservation.cta")}
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
